import React, { useState, useEffect } from 'react';
import { 
  Box, Grid, Card, CardContent, Typography, CardActionArea, Divider,
  CircularProgress, Paper, Avatar, List, ListItem, ListItemText, ListItemAvatar,
  Chip, Button, IconButton, LinearProgress, useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import SubjectIcon from '@mui/icons-material/Subject';
import RoomIcon from '@mui/icons-material/Room';
import LinkIcon from '@mui/icons-material/Link';
import ScienceIcon from '@mui/icons-material/Science';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PageHeader from '../components/PageHeader';
import { getDepartments, getSemesters, getTeachers, getSubjects, getRooms, getTeacherSubjects } from '../services/api';
// Import the college logo and timetable image
import collegeLogo from '../assets/images/College_Logo.png';
import timetableImage from '../assets/images/timetable.jpg';

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [stats, setStats] = useState({
    departments: 0,
    semesters: 0,
    teachers: 0,
    subjects: 0,
    rooms: 0,
    teacherSubjects: 0,
    labTeachers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [
          departmentsRes,
          semestersRes,
          teachersRes,
          subjectsRes,
          roomsRes,
          teacherSubjectsRes
        ] = await Promise.all([
          getDepartments(),
          getSemesters(),
          getTeachers(),
          getSubjects(),
          getRooms(),
          getTeacherSubjects()
        ]);

        // Count lab subjects
        const labSubjects = subjectsRes.data.filter(subject => subject.is_lab).length;

        setStats({
          departments: departmentsRes.data.length,
          semesters: semestersRes.data.length,
          teachers: teachersRes.data.length,
          subjects: subjectsRes.data.length,
          labSubjects: labSubjects,
          rooms: roomsRes.data.length,
          teacherSubjects: teacherSubjectsRes.data.length
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Quick actions for the dashboard
  const quickActions = [
    { title: 'Generate Timetable', description: 'Create a new conflict-free timetable', icon: <AutoFixHighIcon />, color: theme.palette.primary.main, path: '/timetable-generator' },
    { title: 'View Timetable', description: 'See existing department timetables', icon: <CalendarViewWeekIcon />, color: theme.palette.secondary.main, path: '/timetable' },
    { title: 'Teacher Timetable', description: 'View schedules by teacher', icon: <EventNoteIcon />, color: theme.palette.success.main, path: '/teacher-timetable' },
  ];

  // Module cards with their stats
  const statCards = [
    { title: 'Departments', stat: stats.departments, description: 'Academic departments in the system', icon: <SchoolIcon />, color: '#1976d2', path: '/departments' },
    { title: 'Teachers', stat: stats.teachers, description: 'Faculty members', icon: <PersonIcon />, color: '#4caf50', path: '/teachers' },
    { title: 'Subjects', stat: stats.subjects, description: `Including ${stats.labSubjects || 0} lab subjects`, icon: <SubjectIcon />, color: '#ff9800', path: '/subjects' },
    { title: 'Rooms', stat: stats.rooms, description: 'Available classrooms and labs', icon: <RoomIcon />, color: '#f44336', path: '/rooms' },
  ];

  // Management modules
  const managementModules = [
    { title: 'Manage Departments', icon: <SchoolIcon />, color: '#1976d2', path: '/departments' },
    { title: 'Manage Semesters', icon: <NoteAltIcon />, color: '#2196f3', path: '/semesters' },
    { title: 'Manage Teachers', icon: <PersonIcon />, color: '#4caf50', path: '/teachers' },
    { title: 'Manage Subjects', icon: <SubjectIcon />, color: '#ff9800', path: '/subjects' },
    { title: 'Manage Rooms', icon: <RoomIcon />, color: '#f44336', path: '/rooms' },
    { title: 'Teacher-Subject Mapping', icon: <LinkIcon />, color: '#9c27b0', path: '/teacher-subjects' },
    { title: 'Lab Teacher Assignment', icon: <ScienceIcon />, color: '#673ab7', path: '/lab-teachers' },
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageHeader 
          title="Dashboard" 
          subtitle=""
          logo={collegeLogo}
          centerImage={timetableImage}
          showLogo={true}
        />
        <LinearProgress />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader 
        title="Dashboard" 
        subtitle=""
        logo={collegeLogo}
        centerImage={timetableImage}
        showLogo={true}
      />

      {/* Quick Actions Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  },
                  height: '100%',
                  display: 'flex'
                }}
              >
                <CardActionArea 
                  component={Link} 
                  to={action.path}
                  sx={{ display: 'flex', height: '100%', p: 0 }}
                >
                  <Box 
                    sx={{ 
                      bgcolor: action.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      width: '80px'
                    }}
                  >
                    {action.icon}
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 500 }}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                    <ArrowForwardIcon color="action" />
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Statistics Cards Section */}
      <Box sx={{ mb: 4 }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
            System Overview
          </Typography>
        </motion.div>
        
        <Grid container spacing={2}>
          {statCards.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.1,  // Staggered delay based on index
                  ease: "easeOut"
                }}
                whileHover={{ 
                  y: -10, 
                  transition: { duration: 0.2 } 
                }}
              >
                <Card sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ 
                        type: 'spring',
                        stiffness: 260,
                        damping: 20,
                        delay: 0.1 + index * 0.1
                      }}
                    >
                      <Avatar sx={{ bgcolor: item.color, mr: 2, width: 56, height: 56 }}>
                        {item.icon}
                      </Avatar>
                    </motion.div>
                    <Box>
                      <Typography variant="h6" component="div">
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                    >
                      <Typography variant="h4" component="div" color="text.primary">
                        {item.stat}
                      </Typography>
                    </motion.div>
                    <Button 
                      component={Link} 
                      to={item.path} 
                      variant="text" 
                      endIcon={<ArrowForwardIcon />}
                    >
                      Details
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Management Modules Section */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
          Management Modules
        </Typography>
        
        <List sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2
        }}>
          {managementModules.map((module, index) => (
            <Paper
              key={index}
              component={Link}
              to={module.path}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderRadius: 2,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.03)',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: module.color }}>
                  {module.icon}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={module.title} />
              <IconButton size="small">
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default Dashboard;
